import React from "react"
import { graphql } from "gatsby"
import Layout from "../components/layout"
import { Disclaimer, H2, H4, H5, P, Ul } from "../components/el/typography"
import {
  PageButton,
  PillLink,
  TabLink,
  ProdCTAlink,
} from "../components/el/button"
import {
  Block,
  Section,
  Header,
  Card,
  CardLi,
  CardSub,
  CardBody,
  CardImage,
  CardWrap,
  PillRow,
} from "../components/ui/mastercard"
import LowEvap from "../components/svg/tic/lowEvap"
import Extreme from "../components/svg/tic/extreme"
import Nano from "../components/svg/tic/nano"
import Fv from "../components/svg/tic/fv"
import UyfIcon from "../components/svg/tic/uyfIcon"
import OgIcon from "../components/svg/tic/ogIcon"
import WpIcon from "../components/svg/tic/wpIcon"
import { ContactCTA } from "../components/ui/cta"
import useRemoveBackToTop from "../components/useRemoveBackToTop"
import HeroSection from "../components/ui/herosection"
import { Seo } from "../components/seo"
import VideoTestimonial from "../components/ui/videoTestimonial"

const schema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://htslabs.com",
  name: "Thomson | Solutions At Work",
  legalName: "Thomson Instrument Company",
  url: "https://htslabs.com",
  logo: "https://htslabs.com/TIC_LOGO.png",
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer service",
      telephone: "+1(760)757-8080",
      email: "customerservice@htslabs.com",
    },
  ],
  address: {
    "@type": "PostalAddress",
    streetAddress: "2091 Rutherford Rd., Suite 150",
    addressLocality: "Carlsbad",
    addressRegion: "CA",
    postalCode: "92008",
    addressCountry: "USA",
  },
  foundingDate: "1970",
  sameAs: ["https://www.linkedin.com/company/thomson-instrument/"],
}
const App = ({ data }) => {
  const { flask, plate, filter, logo, sixwell } = data
  useRemoveBackToTop()

  const testimonials = [
    {
      videoId: "1098034178",
      color: "purple",
      quote: "7L Flasks are better than wave bags!",
      author: "Brandon Chin",
      company: "Intellia Therapeutics, Inc.",
      location: "PEGS",
      linkTo: "/og/seven",
      linkText: "More on 7L Flasks",
    },
    {
      videoId: "1098034191",
      color: "indigo",
      quote: "Ultra Yield® System is the BEST!",
      author: "Fabio Fonseca",
      company: "Bionova Scientific",
      location: "ASGCT",
      linkTo: "/uyf",
      linkText: "More on Ultra Yield®",
    },
    {
      videoId: "1098034168",
      color: "purple",
      quote: "Thomson flasks changed my life!",
      author: "Paul Wan",
      company: "Viva Biotech",
      location: "ASGCT",
      linkTo: "/og",
      linkText: "More on Optimum Growth®",
    },
    {
      videoId: "1098034207",
      color: "purple",
      quote: "I love your Optimum Growth® Flasks!",
      author: "Thera Mulvania",
      company: "Expression Systems",
      location: "ASGCT",
      linkTo: "/og",
      linkText: "More on Optimum Growth®",
    },
  ]

  return (
    <Layout noheader>
      <HeroSection />

      {/* ===========================================================================
===============VIDEO TESTIMONIALS SECTION=====================
=========================================================================== */}
      <Block theme="darkfull">
        <Header center snug>
          <H2 className="text-white">Video Testimonials</H2>
          <H5 className="text-slate-300">What Our Customers Are Saying</H5>
          <P className="max-w-2xl mx-auto text-slate-400">
            Hear from our customers about how Thomson's products have
            transformed their workflows and improved their results.
          </P>
        </Header>
        <Section general tight>
          <div className="w-full grid px-8 pt-8 pb-10 gap-12 lg:gap-8 xl:px-12 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 bg-white p-4 rounded-lg shadow-lg">
            {testimonials.map((testimonial, index) => (
              <VideoTestimonial
                key={index}
                videoId={testimonial.videoId}
                quote={testimonial.quote}
                author={testimonial.author}
                company={testimonial.company}
                location={testimonial.location}
                color={testimonial.color}
                linkText={testimonial.linkText}
                linkTo={testimonial.linkTo}
              />
            ))}
          </div>
          <Disclaimer className="text-slate-300 text-center">
            Thomson is not affiliated with Intellia Therapeutics, Bionova
            Scientific, Viva Biotech and Expression Systems
          </Disclaimer>
        </Section>
      </Block>

      {/* Luke Rodda video */}
      <Block theme="darkbottom">
        <Header center tight>
          <H2 className="mx-auto max-w-3xl">
            Dr. Luke Rodda's Great Talk at CAT Conference on eXtreme|FV®
          </H2>
        </Header>
        <Section onecard snug>
          <CardBody className="col-span-8">
            <div className="grid grid-cols-1 md:grid-cols-8 gap-4">
              <div className="col-span-1 md:col-span-3">
                <CardSub title="Using eXtreme|FV® for Sample Preparation" />
                <P>
                  Dr. Luke Rodda is Chief Forensic Toxicologist and Director of
                  Forensic Laboratory at San Francisco Office of the Chief
                  Medical Examiner, and he talks about the benefits of using
                  eXtreme|FV®. Thomson eXtreme|FV® offer multi-layer
                  filtration for viscous samples and samples containing up to
                  30% particulates.
                </P>
                <div className="mt-4">
                  <div className="mt-4">
                    <ProdCTAlink to="/fv/extreme" color="teal" svg="FV">
                      Learn more about eXtreme|FV®
                    </ProdCTAlink>
                    <div className="mt-4">
                      <ProdCTAlink
                        to="/technical/postmortem-toxicology"
                        color="teal"
                      >
                        Read About Postmortem Toxicology by&nbsp;Dr.&nbsp;Rodda
                      </ProdCTAlink>
                    </div>
                  </div>
                </div>
                <Disclaimer>
                  Thomson is not affiliated with Dr. Luke Rodda.
                </Disclaimer>
              </div>
              <div className="col-span-1 md:col-span-5 overflow-hidden rounded-lg shadow-md">
                <iframe
                  className="aspect-video w-full mx-auto"
                  src="https://player.vimeo.com/video/1099477567"
                  allow="autoplay; fullscreen"
                  title="Using eXtreme|FV® for Sample Preparation"
                ></iframe>
              </div>
            </div>
          </CardBody>
        </Section>
      </Block>

      {/* 6-well */}
      <Block theme="whitefull">
        <Header center snug>
          <H2>Optimum Growth® 6-Well platform</H2>
          <H5>A Mid-Scale Bioprocessing Solution</H5>
          <div className="">
            <span className="text-center inline-block text-xl font-bold text-amber-400 px-4 py-1 mt-3.5 rounded-lg bg-blue-800 shadow-md animate-pulse">
              New Product
            </span>
          </div>
        </Header>
        <Section onecard tight>
          <CardBody className="col-span-5">
            <H5>Designed for working volumes 20mL to 50mL</H5>
            <P>
              The Thomson 6-Well Plate with the integrative cap, is designed for
              mid-scale cell culture, supporting larger working volumes. The
              platform's integrated design ensures stable, contamination-free
              conditions that are essential for high quality bioprocessing
              applications, making it ideal for scaling CHO, HEK293, or insect
              cell workflows.
            </P>

            <CardSub title="Key Features" />
            <Ul>
              <CardLi circle>
                <b>Integrated Lid:</b> Best use is 1-7-day growth
              </CardLi>
              <CardLi circle>
                <b>Large Working Volume Range:</b> Supports 20mL to 50mL, ideal
                for flexible scale-up
              </CardLi>
              <CardLi circle>
                <b>High Throughput:</b> Accommodates 24 plates per tray with
                universal shaker compatibility
              </CardLi>
              <CardLi circle>
                <b>User-Friendly:</b> Eliminates spin tube handling issues like
                wrist strain from uncapping
              </CardLi>
            </Ul>
            <div className="mt-4">
              <div className="mt-5 xl:mt-8 xl:w-1/2">
                <ProdCTAlink to="/wp/sixwell" color="pink" svg="WP">
                  Learn More About 6-Well Plates
                </ProdCTAlink>
              </div>
            </div>
          </CardBody>
          <CardImage
            alt={sixwell.name}
            className="col-span-3 bg-white"
            cover
            gatsbyImageData={sixwell.childImageSharp.gatsbyImageData}
          />
        </Section>
      </Block>

      {/* ===========================================================================
===============SHAKE FLASK WELL PLATE FILTER PLATE SECTION=====================
=========================================================================== */}
      <Block theme="lightbottom">
        <Header center normal>
          <H2>Innovative Single-Use Solutions</H2>
          <P className="mx-auto max-w-2xl">
            Thomson delivers practical scientific innovations enabling
            scientific advancements in pharmaceutical, biotech,
            environmental/food, toxicology/forensics, and contract manufacturing
            industries by helping to improve cell growth, purification, and
            analysis.
          </P>
        </Header>
        <Section threecard normal>
          <Card
            alt={flask.name}
            threecard
            gatsbyImageData={flask.childImageSharp.gatsbyImageData}
          >
            <CardWrap
              loose
              footer={
                <>
                  <TabLink
                    className="mr-3"
                    to="/uyf"
                    color="indigo"
                    svg={<UyfIcon />}
                  />{" "}
                  <TabLink to="/og" color="purple" svg={<OgIcon />} />
                </>
              }
            >
              <PillRow>
                <PillLink
                  to="/uyf"
                  color="indigo"
                  className="mr-3 mt-2 xl:mt-0"
                >
                  {logo.siteMetadata.brand.uyf} System
                </PillLink>
                <PillLink to="/og" color="purple" className="mt-2 xl:mt-0">
                  {logo.siteMetadata.brand.og} System
                </PillLink>
              </PillRow>
              <H4>Shake Flasks</H4>
              <P>
                Thomson offers a full line of shake flasks and accessories to
                help provide higher cell, protein and DNA yields. Shake Flasks
                are designed specifically for insect/mammalian, or microbial/
                <i>E. coli</i> based on an understanding and experience of lab
                operations.
              </P>
            </CardWrap>
          </Card>

          <Card
            alt={plate.name}
            threecard
            gatsbyImageData={plate.childImageSharp.gatsbyImageData}
          >
            <CardWrap
              loose
              footer={<TabLink to="/wp" color="pink" svg={<WpIcon />} />}
            >
              <PillRow>
                <PillLink to="/wp" color="pink" className="mt-2 xl:mt-0">
                  Well Plates
                </PillLink>
              </PillRow>
              <H4>Well Plates</H4>
              <P>
                Our well plate catalog continues to grow and offer the highest
                quality well plates ready for robotics, cell culture, synthesis
                or analysis.
              </P>
            </CardWrap>
          </Card>

          <Card
            alt={filter.name}
            threecard
            gatsbyImageData={filter.childImageSharp.gatsbyImageData}
          >
            <CardWrap
              loose
              footer={<TabLink to="/fp" color="pink" svg={<WpIcon />} />}
            >
              <PillRow>
                <PillLink to="/fp" color="pink" className="mt-2 xl:mt-0">
                  Filter Plates
                </PillLink>
              </PillRow>
              <H4>Filter Plates</H4>
              <P>
                Whether your applications are analytical or biological Thomson
                has filter plates available in both 96 and 24-well
                configurations. Pair filter plates with well plates for sample
                collection.
              </P>
            </CardWrap>
          </Card>
        </Section>
      </Block>

      {/* ===========================================================================
========================FILTER VIALS SECTION===============================
=========================================================================== */}
      <Block theme="lightfull">
        <Section noheader general grid gap normal>
          <div className="col-span-4">
            <H2 className="mt-0 lg:mt-5">
              Scientists discover new ways to use Filter Vials everyday!
            </H2>

            <P className="max-w-3xl">
              Thomson's filter vial line is a proven indispensable tool for
              sample prep and analysis for all types of HPLC, GC, LC-MS, GC-MS
              and lateral flow analyses. Whether testing pharmaceuticals,
              toxicology or other areas, filter vials allow you to go from
              unfiltered samples to filtered samples in 15 seconds.
            </P>

            <div className="mt-3 sm:flex">
              <PageButton dark to="/fv">
                More About Filter Vials
              </PageButton>
              <PageButton light to="/faqfv/" className="mt-3 sm:ml-3 sm:mt-0">
                General Info
              </PageButton>
            </div>
          </div>
          <div className="col-span-4 mt-8 grid grid-cols-2 gap-0.5 text-slate-400 lg:mt-0">
            <div className="col-span-1 flex items-center justify-center bg-slate-50 px-8 py-8">
              <Fv className="max-h-12 w-full fill-current"></Fv>
            </div>
            <div className="col-span-1 flex items-center justify-center bg-slate-50 px-8 py-8">
              <LowEvap className="max-h-12 w-full fill-current"></LowEvap>
            </div>

            <div className="col-span-1 flex items-center justify-center bg-slate-50 px-8 py-8">
              <Nano className="max-h-12 w-full fill-current"></Nano>
            </div>
            <div className="col-span-1 flex items-center justify-center bg-slate-50 px-8 py-8">
              <Extreme className="max-h-12 w-full fill-current"></Extreme>
            </div>
          </div>
        </Section>
      </Block>

      {/* ===========================================================================
========================Columns SECTION===============================
=========================================================================== */}
      <Block theme="whitefull">
        <Header normal></Header>

        <Section general flex normal noheader>
          <div>
            <H2>{logo.siteMetadata.brand.singlestep} Empty Columns</H2>
            <H5 className="mt-0 sm:mt-2">
              Ready for sorbents & resins, comes in variety of sizes
            </H5>
          </div>
          <div className="mt-8 flex lg:mt-0 lg:shrink-0">
            <PageButton dark to="/columns">
              Get started
            </PageButton>
          </div>
        </Section>
      </Block>
      {/* CTA */}
      <ContactCTA
        to="/"
        cta="We're here to help"
        title={logo.siteMetadata.company.tag}
        button="Let's talk"
      >
        Thomson has a nationwide team of expert technical support staff ready to
        tackle any questions you may have regarding our products.
      </ContactCTA>
    </Layout>
  )
}

export const query = graphql`
  {
    flask: file(relativePath: { eq: "page/ultra-yield-related.jpg" }) {
      name
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, layout: CONSTRAINED)
      }
    }
    plate: file(relativePath: { eq: "page/WP-related.jpg" }) {
      name
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, layout: CONSTRAINED)
      }
    }
    filter: file(relativePath: { eq: "page/filter-plate.jpg" }) {
      name
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, layout: CONSTRAINED)
      }
    }
    sixwell: file(relativePath: { eq: "page/6-well_marketing.jpg" }) {
      name
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, layout: CONSTRAINED)
      }
    }

    logo: site {
      siteMetadata {
        company {
          tag
        }
        brand {
          uyf
          singlestep
          rc
          plasmid
          og
          nano
          extreme
          airotop
        }
      }
    }
  }
`
export default App

export const Head = () => (
  <Seo>
    <script key="schema" type="application/ld+json">
      {JSON.stringify({ schema })}
    </script>
  </Seo>
)
